import { MutationHookOptions, MutationTuple, useMutation } from '@apollo/client'
import {
  IIdentifyAndCreateSessionMutationParams,
  IIdentifyAndCreateSessionMutationResponse,
} from './types'
import identifyAndCreateSessionMutation from './mutation'

/**
 * Hook declaration for the `identifyAndCreateSession` mutation.
 *
 * `no-cache` like `createOrder` beside it: the returned session is written
 * into `defaultSlice` through `setSessionId` and `setSessionData`, and every
 * downstream query re-fetches on `network-only`, so a normalised copy here
 * would only be a staler second home for the same entity.
 * @internal
 */
/*
 * ⚠️ **Do not set `errorPolicy: 'all'` or an `onError` handler here.**
 *
 * Under the default policy the mutate function *rejects*, and
 * `useIdentifyAndCreateSession` reads every failure in its catch — which is
 * where the backend's generic error codes are classified into
 * `setLoginError` vs. `setLoginRateLimited`. Either of those options makes
 * the mutation resolve with `FetchResult.errors` instead; the catch never
 * runs, the hook sets no state, and the shopper sees a submit button that
 * silently cleared with no message.
 */
const useIdentifyAndCreateSessionMutation = (
  options?: MutationHookOptions<
    IIdentifyAndCreateSessionMutationResponse,
    IIdentifyAndCreateSessionMutationParams
  >,
): MutationTuple<
  IIdentifyAndCreateSessionMutationResponse,
  IIdentifyAndCreateSessionMutationParams
> => {
  return useMutation<
    IIdentifyAndCreateSessionMutationResponse,
    IIdentifyAndCreateSessionMutationParams
  >(identifyAndCreateSessionMutation, {
    fetchPolicy: 'no-cache',
    ...options,
  })
}

export default useIdentifyAndCreateSessionMutation
